import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';

// Material UI components
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  Link
} from '@mui/material';

// Material UI icons
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Link as LinkIcon
} from '@mui/icons-material';

// Services
import {deleteSupplierPart } from '../../services/supplierService';

// Components
import SupplierPartEditDialog from './SupplierPartEditDialog';

const SupplierPartGrid = ({ supplierId, supplierParts, onPartsChange }) => {
  // State for grid
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('part.internal_part_number');
  const [filterText, setFilterText] = useState('');
  const [debouncedFilterText, setDebouncedFilterText] = useState('');

  // Debounce filter text changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedFilterText(filterText);
    }, 500); // Increased to 500ms for consistency

    return () => clearTimeout(timeoutId);
  }, [filterText]);

  // State for dialogs
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);

  // Handle sorting
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle filtering
  const handleFilterChange = (event) => {
    setFilterText(event.target.value);
    setPage(0);
  };

  // Handle edit dialog
  const handleEditClick = (part) => {
    setSelectedPart(part);
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedPart(null);
  };

  const handleEditSuccess = () => {
    setEditDialogOpen(false);
    setSelectedPart(null);
    if (onPartsChange) {
      onPartsChange();
    }
    toast.success('Supplier part updated successfully');
  };

  // Handle delete dialog
  const handleDeleteClick = (part) => {
    setSelectedPart(part);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedPart(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteSupplierPart(supplierId, selectedPart.part_id);
      if (onPartsChange) {
        onPartsChange();
      }
      toast.success('Part removed from supplier successfully');
      setDeleteDialogOpen(false);
      setSelectedPart(null);
    } catch (error) {
      toast.error(error.message || 'Failed to remove part from supplier');
    }
  };

  // Filter and sort data
  const filteredData = supplierParts.filter((part) => {
    const searchText = debouncedFilterText.toLowerCase();

    // Check if any UPC code matches the search text
    const upcMatches = part.upcs?.some(upc =>
      upc.upc_code.toLowerCase().includes(searchText)
    );

    return (
      part.part?.internal_part_number?.toLowerCase().includes(searchText) ||
      part.part?.description?.toLowerCase().includes(searchText) ||
      part.supplier_part_number?.toLowerCase().includes(searchText) ||
      upcMatches
    );
  });

  const sortedData = filteredData.sort((a, b) => {
    let aValue, bValue;

    // Handle nested properties
    if (orderBy === 'part.internal_part_number') {
      aValue = a.part?.internal_part_number || '';
      bValue = b.part?.internal_part_number || '';
    } else if (orderBy === 'part.description') {
      aValue = a.part?.description || '';
      bValue = b.part?.description || '';
    } else {
      aValue = a[orderBy] || '';
      bValue = b[orderBy] || '';
    }

    // Handle numeric values
    if (orderBy === 'cost' || orderBy === 'lead_time_days') {
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
    }

    // Sort
    if (order === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return bValue < aValue ? -1 : bValue > aValue ? 1 : 0;
    }
  });

  // Paginate data
  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {/* Toolbar with title, filter, and add button */}
      <Toolbar
        sx={{
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
          bgcolor: 'background.paper',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`
        }}
      >
        <Typography
          sx={{ flex: '1 1 100%' }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          Parts Supplied
        </Typography>

        <TextField
          size="small"
          variant="outlined"
          placeholder="Filter parts..."
          value={filterText}
          onChange={handleFilterChange}
          sx={{ mr: 2, width: 250 }}
          // Using startAdornment directly instead of deprecated InputProps
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          }
        />

        <Tooltip title="Add Part to Supplier">
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            component={RouterLink}
            to={`/suppliers/${supplierId}/add-part`}
            size="small"
          >
            Add Part
          </Button>
        </Tooltip>
      </Toolbar>

      {/* Table */}
      <TableContainer>
        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'part.internal_part_number'}
                  direction={orderBy === 'part.internal_part_number' ? order : 'asc'}
                  onClick={() => handleRequestSort('part.internal_part_number')}
                >
                  Part Number
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'part.description'}
                  direction={orderBy === 'part.description' ? order : 'asc'}
                  onClick={() => handleRequestSort('part.description')}
                >
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'supplier_part_number'}
                  direction={orderBy === 'supplier_part_number' ? order : 'asc'}
                  onClick={() => handleRequestSort('supplier_part_number')}
                >
                  Supplier Part #
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'cost'}
                  direction={orderBy === 'cost' ? order : 'asc'}
                  onClick={() => handleRequestSort('cost')}
                >
                  Cost
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={orderBy === 'lead_time_days'}
                  direction={orderBy === 'lead_time_days' ? order : 'asc'}
                  onClick={() => handleRequestSort('lead_time_days')}
                >
                  Lead Time (Days)
                </TableSortLabel>
              </TableCell>
              <TableCell>URL</TableCell>
              <TableCell>UPC Codes</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length > 0 ? (
              paginatedData.map((part) => (
                <TableRow key={part.id} hover>
                  <TableCell>
                    <RouterLink
                      to={`/inventory/${part.part_id}`}
                      style={{
                        color: 'inherit',
                        textDecoration: 'none',
                        fontWeight: 500
                      }}
                    >
                      {part.part?.internal_part_number || 'N/A'}
                    </RouterLink>
                  </TableCell>
                  <TableCell>{part.part?.description || 'N/A'}</TableCell>
                  <TableCell>{part.supplier_part_number}</TableCell>
                  <TableCell align="right">${parseFloat(part.cost).toFixed(2)}</TableCell>
                  <TableCell align="right">{part.lead_time_days || 'N/A'}</TableCell>
                  <TableCell>
                    {part.url ? (
                      <Link
                        href={part.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'flex', alignItems: 'center' }}
                      >
                        <LinkIcon fontSize="small" sx={{ mr: 0.5 }} />
                        View
                      </Link>
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  <TableCell>
                    {part.upcs && part.upcs.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {part.upcs.map((upc) => (
                          <Typography key={upc.id} variant="body2" sx={{
                            fontWeight: upc.is_primary ? 'bold' : 'normal',
                            color: upc.is_primary ? 'primary.main' : 'text.primary'
                          }}>
                            {upc.upc_code} {upc.is_primary && '(Primary)'}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      'None'
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title="View Part Details">
                        <IconButton
                          size="small"
                          component={RouterLink}
                          to={`/inventory/${part.part_id}`}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Supplier Part">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditClick(part)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove Part from Supplier">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(part)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  {filterText ? (
                    <Typography variant="body2" color="text.secondary">
                      No parts match the filter criteria
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No parts associated with this supplier yet
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Edit Dialog */}
      {selectedPart && (
        <SupplierPartEditDialog
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          onSuccess={handleEditSuccess}
          supplierId={supplierId}
          supplierPart={selectedPart}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
      >
        <DialogTitle>Remove Part from Supplier</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove this part from the supplier? This action cannot be undone.
          </DialogContentText>
          {selectedPart && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">
                Part: {selectedPart.part?.internal_part_number} - {selectedPart.part?.description}
              </Typography>
              <Typography variant="subtitle2">
                Supplier Part #: {selectedPart.supplier_part_number}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SupplierPartGrid;
